// Policies hub — customer-facing catalog and the pure display logic.
//
// The API (GET /v1/orgs/:orgId/policy-inventory) decides which rule sets
// the org has, is locked out of, or can't see, and returns real counts.
// This module names each rule set, says where it lives, and turns the
// counts into the short summary the hub shows. No React and no network
// here, so it's unit-tested (catalog.test.ts).

export type PolicyCategory =
  | 'detection'
  | 'checks'
  | 'exceptions'
  | 'responses'
  | 'approvals'
  | 'identity'
  | 'guardrails';

export type PolicyAppKey =
  | 'xes'
  | 'posture'
  | 'agents'
  | 'insider'
  | 'surface'
  | 'resilience'
  | 'ai-gateway'
  | 'organization';

export interface StateCounts {
  on?: number;
  testing?: number;
  off?: number;
}

/** One entry of the API response (mirrors apps/api/src/lib/policyInventory.ts). */
export interface PolicyInventorySource {
  key: string;
  category: PolicyCategory;
  app: PolicyAppKey;
  available: boolean;
  reason?: 'not_entitled' | 'not_built';
  counts?: StateCounts;
  total?: number;
  breakdown?: Array<{ label: string; count: number }>;
  lastChangedAt?: number | null;
}

export interface PolicyInventory {
  generatedAt: number;
  sources: PolicyInventorySource[];
  failures: Array<{ key: string; message: string }>;
}

export const CATEGORY_ORDER: PolicyCategory[] = [
  'detection',
  'checks',
  'exceptions',
  'responses',
  'approvals',
  'identity',
  'guardrails',
];

export const CATEGORY_COPY: Record<PolicyCategory, { title: string; description: string }> = {
  detection: { title: 'Detection rules', description: 'What gets flagged as a threat.' },
  checks: { title: 'Checks and compliance', description: 'How your systems are measured against good practice and standards.' },
  exceptions: { title: 'Exceptions', description: 'Issues you’ve knowingly accepted, and for how long.' },
  responses: { title: 'Automated responses', description: 'What happens automatically when something is detected.' },
  approvals: { title: 'Approvals', description: 'Actions that wait for a person to approve them.' },
  identity: { title: 'Identity and access', description: 'How people sign in and how risk is scored.' },
  guardrails: { title: 'AI and data guardrails', description: 'Limits on how AI and your data are used.' },
};

export const APP_LABEL: Record<PolicyAppKey, string> = {
  xes: 'Email Security',
  posture: 'Posture',
  agents: 'Agents',
  insider: 'Insider',
  surface: 'Surface',
  resilience: 'Resilience',
  'ai-gateway': 'AI Gateway',
  organization: 'Organization',
};

/** Product name used in the "contact sales" subject for a locked app. */
export const APP_SKU: Partial<Record<PolicyAppKey, string>> = {
  xes: 'XES',
  posture: 'Posture',
  agents: 'Agents',
  insider: 'Insider',
  surface: 'Surface',
  resilience: 'Resilience',
  'ai-gateway': 'AI Gateway',
};

export interface PolicySourceCopy {
  name: string;
  description: string;
  /** Owning page. */
  href: string;
  /** What the rule set adds, shown when the org doesn't have it. */
  lockedText?: string;
  /** Where "Learn more" goes when locked; defaults to contacting sales. */
  lockedHref?: string;
  /** Single on/off setting rather than a list of rules. */
  setting?: boolean;
  /** Unit for `total` when it isn't a count of rules (e.g. frameworks). */
  totalLabel?: string;
}

export const SOURCE_COPY: Record<string, PolicySourceCopy> = {
  'xes.detection-rules': {
    name: 'Detection rules',
    description: 'Rules that check every incoming email alongside the AI verdict.',
    href: '/xes/rules',
    lockedText: 'Adds rules that check every incoming email for phishing, fraud and malware.',
  },
  'xes.sender-rules': {
    name: 'Sender allow and block list',
    description: 'Senders whose email is always let through or always quarantined.',
    href: '/xes/settings/rules',
    lockedText: 'Adds a list of senders to always allow or always block.',
  },
  'posture.cloud-checks': {
    name: 'Cloud checks',
    description: 'Checks run against your cloud accounts.',
    href: '/posture/iaas/checks',
    lockedText: 'Adds checks for misconfigured cloud accounts and services.',
  },
  'posture.iac-checks': {
    name: 'Infrastructure-as-code checks',
    description: 'Checks run against your infrastructure code before it ships.',
    href: '/posture/iac/checks',
    lockedText: 'Adds checks that catch risky settings in infrastructure code.',
  },
  'posture.saas-checks': {
    name: 'SaaS checks',
    description: 'Checks run against business apps such as Microsoft 365 and Salesforce.',
    href: '/posture/saas/checks',
    lockedText: 'Adds checks for risky settings in business apps.',
  },
  'posture.virtualization-checks': {
    name: 'Virtualization checks',
    description: 'Checks run against your virtualization platforms.',
    href: '/posture/hypervisor/checks',
    lockedText: 'Adds checks for your virtualization platforms.',
  },
  'posture.device-policies': {
    name: 'Device policies',
    description: 'Rules that flag devices failing the checks you care about.',
    href: '/posture/policies',
    lockedText: 'Adds rules that flag devices missing encryption, updates or other protections.',
  },
  'posture.compliance': {
    name: 'Compliance frameworks',
    description: 'Standards such as ISO 27001 and SOC 2, and which checks count toward each control.',
    href: '/posture/iaas/compliance',
    lockedText: 'Adds compliance scoring against standards such as ISO 27001 and SOC 2.',
    totalLabel: 'frameworks',
  },
  'posture.exemptions': {
    name: 'Exemptions',
    description: 'Issues you’ve accepted for a set time, with the reason why.',
    href: '/posture/exemptions',
    lockedText: 'Adds time-limited exemptions for issues you choose to accept.',
  },
  'identity.automated-responses': {
    name: 'Automated responses',
    description: 'Rules that act on risky behavior, such as assigning training or suspending access.',
    href: '/policies/automated-responses',
    lockedText: 'Part of the Identity Governance add-on: act automatically on risky behavior, such as repeated phishing clicks.',
    lockedHref: '/policies/automated-responses',
  },
  'xes.auto-assign-training': {
    name: 'Training after a blocked email',
    description: 'Training assigned automatically to people who were sent a quarantined email.',
    href: '/xes/settings/training',
    lockedText: 'Adds automatic training for people targeted by blocked emails.',
  },
  'agents.approvals': {
    name: 'Agent action approvals',
    description: 'Agent and workflow actions that wait for an admin to approve them.',
    href: '/agents/approvals',
    lockedText: 'Adds agents that respond to events, with approval before they act.',
  },
  'identity.mfa': {
    name: 'Multi-factor sign-in',
    description: 'Whether members can add a second sign-in step with an authenticator app.',
    href: '/identity/mfa',
    setting: true,
  },
  'risk.weights': {
    name: 'Risk score weights',
    description: 'How much each kind of behavior adds to or takes from a person’s risk score.',
    href: '/risk',
  },
  'ai-gateway.policies': {
    name: 'AI Gateway policies',
    description: 'Rules for how your organization uses AI, managed in AI Gateway.',
    href: '/gateway/policies',
    lockedText: 'Adds controls over which AI tools are used and what data reaches them.',
  },
  'insider.rules': {
    name: 'Insider risk rules',
    description: 'Rules for spotting risky activity by people inside your organization.',
    href: '/insider',
  },
  'surface.rules': {
    name: 'Attack surface rules',
    description: 'Rules for what counts as an exposed or unmanaged asset.',
    href: '/surface/findings',
  },
  'resilience.retention': {
    name: 'Backup retention policies',
    description: 'How long backups are kept and who must approve changes.',
    href: '/resilience',
  },
};

// ---------- display ----------

export type RowStatus = 'ready' | 'locked' | 'not_built' | 'failed';

export function rowStatus(source: PolicyInventorySource, failed: ReadonlySet<string>): RowStatus {
  if (!source.available) return source.reason === 'not_built' ? 'not_built' : 'locked';
  if (failed.has(source.key)) return 'failed';
  return 'ready';
}

export interface SummaryPart {
  text: string;
  /** Semantic dot for on/testing/off; none for other figures. */
  state?: 'on' | 'testing' | 'off';
}

const fmt = (n: number) => n.toLocaleString('en-US');

/** The short "42 on · 3 testing · 2 off" summary, built only from figures
 *  the API returned. Empty when there's nothing to say (locked, failed,
 *  or a source with no counts by design). */
export function summarize(source: PolicyInventorySource, copy: PolicySourceCopy | undefined): SummaryPart[] {
  const c = source.counts;
  if (copy?.setting && c) {
    return (c.on ?? 0) > 0 ? [{ text: 'On', state: 'on' }] : [{ text: 'Off', state: 'off' }];
  }
  if (source.total === 0 && !copy?.totalLabel) return [{ text: 'None set up yet' }];
  const parts: SummaryPart[] = [];
  if (c) {
    for (const s of ['on', 'testing', 'off'] as const) {
      const n = c[s];
      if (n !== undefined) parts.push({ text: `${fmt(n)} ${s}`, state: s });
    }
  } else if (source.total !== undefined && copy?.totalLabel) {
    parts.push({ text: `${fmt(source.total)} ${copy.totalLabel}` });
  }
  for (const b of source.breakdown ?? []) parts.push({ text: `${fmt(b.count)} ${b.label}` });
  return parts;
}

// ---------- filters ----------

export type StateFilter = 'all' | 'on' | 'testing' | 'off' | 'locked' | 'not_built';

export const STATE_FILTER_LABEL: Record<StateFilter, string> = {
  all: 'All states',
  on: 'Has rules on',
  testing: 'Has rules testing',
  off: 'Has rules off',
  locked: 'Locked',
  not_built: 'Not available yet',
};

export interface HubFilters {
  app: PolicyAppKey | 'all';
  state: StateFilter;
  query: string;
}

export function matchesFilters(
  source: PolicyInventorySource,
  filters: HubFilters,
  failed: ReadonlySet<string>,
): boolean {
  if (filters.app !== 'all' && source.app !== filters.app) return false;
  const status = rowStatus(source, failed);
  if (filters.state === 'locked' && status !== 'locked') return false;
  if (filters.state === 'not_built' && status !== 'not_built') return false;
  if (filters.state === 'on' || filters.state === 'testing' || filters.state === 'off') {
    if (status !== 'ready' || !((source.counts?.[filters.state] ?? 0) > 0)) return false;
  }
  const q = filters.query.trim().toLowerCase();
  if (q) {
    const copy = SOURCE_COPY[source.key];
    const hay = [copy?.name, copy?.description, APP_LABEL[source.app]].join(' ').toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

/** Visible rows grouped by category, in category order, empty groups dropped. */
export function groupSources(
  sources: readonly PolicyInventorySource[],
  filters: HubFilters,
  failed: ReadonlySet<string>,
): Array<{ category: PolicyCategory; sources: PolicyInventorySource[] }> {
  return CATEGORY_ORDER.map((category) => ({
    category,
    sources: sources.filter((s) => s.category === category && matchesFilters(s, filters, failed)),
  })).filter((g) => g.sources.length > 0);
}

/** Apps that appear in the inventory, in first-seen order, for the filter. */
export function appsInInventory(sources: readonly PolicyInventorySource[]): PolicyAppKey[] {
  const seen: PolicyAppKey[] = [];
  for (const s of sources) if (!seen.includes(s.app)) seen.push(s.app);
  return seen;
}
